import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { setActiveStudioView } from '../../../../../store/screeningSpaceSlice';
import axios from "axios";
import GetCookie from '../../../../utils/GetCookie';
import ScreeningSpaceSidebar from "./ScreeningSpaceSidebar";
import AbstractsList from "./AbstractsList";
import Screening from "./Screening";
import LinkFullText from "./LinkFullText";
import FullTextList from "./FullTextList";
import PrismaModal from './PrismaModal';
import UniqueStudies from "./UniqueStudies";
import TextractorWorkspace from "../../../textractor/TextractorWorkspace";
import GenbiWorkspace from "../../../genbi/GenbiWorkspace";

const ScreeningSpaceProjectView = ({ slrFabricDetails, onBackToProjects, onCreateProject, genbiEntry, textractorEntry }) => {
  const dispatch = useDispatch();
  const API_BASE_URL = '/pceapi/slr';

  const headers = useMemo(() => ({
    Authorization: GetCookie('access_token')
  }), []);

  // ═══ Redux-persisted state (survives page navigation) ═══
  const activeView = useSelector((state) => state.screeningSpace.activeStudioView);

  // ═══ Local transient state ═══
  const [fabricDetails, setFabricDetails] = useState(null);
  const [loadingFabricDetails, setLoadingFabricDetails] = useState(false);
  const [showPrismaModal, setShowPrismaModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedReviewerSet, setSelectedReviewerSet] = useState("SET_1");
  
  const textractorRef = useRef(null);
  const [isExtractionFormOpen, setIsExtractionFormOpen] = useState(false);

  // Extract fabric_id from slrFabricDetails
  const fabricId = slrFabricDetails?.slr_fabric_id;
  const configId = slrFabricDetails?.config_id;

  // Extract grid_docs_thread_id — passed to Textractor for direct CSV migration
  const gridDocsThreadId = slrFabricDetails?.stages?.general_settings?.data?.grid_docs_thread_id;

  // Extract report_docs_thread_id — passed to GenBI for direct report migration
  const reportDocsThreadId = slrFabricDetails?.stages?.general_settings?.data?.report_docs_thread_id;

  // Fabric-level PICO tags (used in filtering panels)
  const picoJson = fabricDetails?.pico_json || null;

  // Framework badge. SINGLE_AI_ONLY with more than one AI agent is screened by
  // several reviewers, so it is shown as Dual / Multi instead of Single.
  const frameworkBadge = useMemo(() => {
    const fw = fabricDetails?.framework;
    const aiAgents = Math.max(
      Number(fabricDetails?.abstract_screening?.llm_count) || 0,
      Number(fabricDetails?.full_text_screening?.llm_count) || 0,
    );
    switch (fw) {
      case "SINGLE_HUMAN_ONLY":
        return { screening: "Single Screening", reviewer: "Human Only" };
      case "SINGLE_AI_ONLY":
        if (aiAgents >= 3) return { screening: "Multi Screening", reviewer: `AI × ${aiAgents}` };
        if (aiAgents === 2) return { screening: "Dual Screening", reviewer: "AI & AI" };
        return { screening: "Single Screening", reviewer: "AI Only" };
      case "DUAL_HUMAN_AI":
        return { screening: "Dual Screening", reviewer: "Human & AI" };
      case "DUAL_HUMAN_HUMAN":
        return { screening: "Dual Screening", reviewer: "Human & Human" };
      default:
        return { screening: "Single Screening", reviewer: "Not Set" };
    }
  }, [fabricDetails]);

  const fetchFabricDetails = useCallback(async (reviewerSetOverride = selectedReviewerSet) => {
    if (!fabricId) return;

    try {
      const res = await axios.post(
        `${API_BASE_URL}/fabric-details`,
        // Always send the set; the backend only applies it under
        // DUAL_HUMAN_HUMAN. Gating on fabricDetails?.framework here would mean
        // reading the very state this call sets — the first fetch would send
        // null, then the framework landing would re-trigger a second fetch.
        { slr_fabric_id: fabricId, reviewer_set: reviewerSetOverride },
        { headers }
      );

      if (res.data?.status === "success") {
        setFabricDetails(res.data.data);
      }
    } catch (err) {
      console.error("Fabric details fetch failed", err);
    } finally {
      setLoadingFabricDetails(false);
    }
  }, [fabricId, headers, API_BASE_URL, selectedReviewerSet]);

  useEffect(() => {
    setLoadingFabricDetails(true);
    fetchFabricDetails();
  }, [fetchFabricDetails]);

  const isExtraction = activeView === "DATA_EXTRACTION";

  const isReports = activeView === "REPORTS";

  const genbiHeaderLeft = (
    <p className="gradient-font5 ev-fs-17 ev-bold-med mb-0 ps-1">Reports &amp; Synthesis</p>
  );

  const backToOverviewBtn = (
    <button
      className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
      onClick={onBackToProjects}
    >
      <i className="bi bi-arrow-left me-1"></i>
      Back to Overview
    </button>
  );

  const extractionHeaderLeft = (
    <>
      <p className="gradient-font5 ev-fs-17 ev-bold-med mb-0 ps-1">Data Extraction</p>
      {isExtractionFormOpen ? (
        <button
          className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-reg text-nowrap"
          onClick={() => textractorRef.current?.exitForm()}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Back to Sheets
        </button>
      ) : (
        <button
          className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg text-nowrap"
          onClick={() => textractorRef.current?.createModel()}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Create Sheet
        </button>
      )}
    </>
  );

  return (
    <>
      {!isExtraction && !isReports && (
      <div className="row gx-3 d-flex justify-content-between align-items-center mb-2 mt-2">

        {/* Left — Title + Create (above sidebar) */}
        <div className="col-md-3 d-flex align-items-center justify-content-between pe-3">
          <p className="gradient-font5 ev-fs-17 ev-bold-med mb-0 ps-1">
            Screening Space
          </p>

          <button
            className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg"
            onClick={onCreateProject}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Create Project
          </button>
        </div>

         {/* Right — Project pill + Framework + Refresh (above content) */}
        <div className="col-md-9 d-flex align-items-center justify-content-between ps-0">
          <div className="d-flex gap-2 align-items-center">
            <span className="ev-message-card1 d-flex align-items-center ev-radius-large px-3 ev-bold-med">
              {slrFabricDetails?.stages?.general_settings?.data?.name || "SLR Project"}
            </span>

            {/* Framework badge — screening-only context */}
            <div className="ev-message-card2 ev-btn-xsmall d-flex align-items-center ev-radius-large ps-3 py-1 pe-1">
              <span className="ev-small-p-blue ev-bold-med">
                {frameworkBadge.screening}
              </span>
              <div className="ev-message-card5 ev-btn-xsmall ev-radius-large ms-2 px-2 ev-med-bold">
                {frameworkBadge.reviewer}
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 align-items-center">
            {(activeView === "QC" || activeView === "QC_FT") && (
              <button
                className="ev-btn-blue-fill-round ev-btn-reg"
                onClick={() => setShowPrismaModal(true)}
              >
                PRISMA
              </button>
            )}
            <button
              className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-reg"
              onClick={() => {
                setLoadingFabricDetails(true);
                fetchFabricDetails();
              }}
            >
              <i className="bi bi-arrow-repeat"></i> Refresh
            </button>
            {backToOverviewBtn}
          </div>
        </div>
      </div>
      )}

      {isReports ? (
        /* Full width — the SLR sidebar (screening steps) is irrelevant here */
        <GenbiWorkspace
          embedded
          slrEntry={genbiEntry}
          headerActions={backToOverviewBtn}
          onExit={onBackToProjects}
        />
      ) : activeView === "DATA_EXTRACTION" ? (
        /* Full width — the SLR sidebar (screening steps) is irrelevant here */
        <TextractorWorkspace
          ref={textractorRef}
          fabricId={fabricId}
          gridThreadId={gridDocsThreadId}
          slrEntry={textractorEntry}
          embedded
          headerLeft={extractionHeaderLeft}
          headerActions={backToOverviewBtn}
          onFormStateChange={setIsExtractionFormOpen}
        />
      ) : (
      <div className="row gx-3 me-1">
        {/* LEFT SIDEBAR */}
        <div className={sidebarCollapsed ? "ev-sidebar-col-collapsed" : "col-md-3"}>
          <ScreeningSpaceSidebar
            slrFabricId={configId}
            fabricIdForTextractor={fabricId}
            gridDocsThreadId={gridDocsThreadId}
            reportDocsThreadId={reportDocsThreadId}
            framework={fabricDetails?.framework}
            abstractScreening={fabricDetails?.abstract_screening}
            fullTextScreening={fabricDetails?.full_text_screening}
            activeView={activeView}
            onOpenAbstractList={() => dispatch(setActiveStudioView("ABSTRACT_LIST"))}
            onOpenHumanScreening={() => dispatch(setActiveStudioView("HUMAN_SCREENING"))}
            onOpenAiScreening={() => dispatch(setActiveStudioView("AI_SCREENING"))}
            onOpenConflictResolution={() => dispatch(setActiveStudioView("CONFLICT"))}
            onOpenQc={() => dispatch(setActiveStudioView("QC"))}
            onOpenFullTextList={() => dispatch(setActiveStudioView("FULLTEXT_LIST"))}
            onOpenLinkFullText={() => dispatch(setActiveStudioView("LINK_FULL_TEXT"))}
            onOpenHumanFT={() => dispatch(setActiveStudioView("HUMAN_FT"))}
            onOpenAiFT={() => dispatch(setActiveStudioView("AI_FT"))}
            onOpenConflictFT={() => dispatch(setActiveStudioView("CONFLICT_FT"))}
            onOpenQcFT={() => dispatch(setActiveStudioView("QC_FT"))}
            onOpenUniqueStudies={() => dispatch(setActiveStudioView("UNIQUE_STUDIES"))}
            onOpenDataExtraction={() => dispatch(setActiveStudioView("DATA_EXTRACTION"))}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          />
        </div>

        {/* RIGHT CONTENT */}
        {/* vh-80 matches the sidebar card, which is also vh-80. Without it this
            column had no height at all and simply grew to fit whatever the
            active view rendered — so each view lined up with the sidebar only
            by coincidence. d-flex flex-column gives the child's h-100 a
            definite height to fill. */}
        <div className={`${sidebarCollapsed ? "col" : "col-md-9"} ev-card-4 pt-2 pb-1 vh-80 d-flex flex-column`}>
          {loadingFabricDetails ? (
            <div className="h-100 d-flex justify-content-center align-items-center">
              <p className="ev-small-p-blue">Loading project details...</p>
            </div>
          ) : (
            <>
              {activeView === "ABSTRACT_LIST" && (
                // pass onBackToProjects so AbstractsList can redirect on setup error
                <AbstractsList
                  fabricId={fabricId}
                  picoJson={picoJson}
                  onBackToProjects={onBackToProjects}
                />
              )}

              {activeView === "HUMAN_SCREENING" && (
                <Screening
                  mode="HUMAN"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  isOwner={fabricDetails?.user_reviewer_status?.is_owner}
                  reviewerSet={selectedReviewerSet}
                  onReviewerSetChange={(nextSet) => { setSelectedReviewerSet(nextSet); fetchFabricDetails(nextSet); }}
                  stats={fabricDetails?.abstract_screening?.human_screening}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "AI_SCREENING" && (
                <Screening
                  mode="AI"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  stats={{
                    results: fabricDetails?.abstract_screening?.ai_screening?.total,
                    included: fabricDetails?.abstract_screening?.ai_screening?.included,
                    excluded: fabricDetails?.abstract_screening?.ai_screening?.excluded,
                    completed: fabricDetails?.abstract_screening?.ai_screening?.reviewed,
                    running: 0,
                    pending: fabricDetails?.abstract_screening?.ai_screening?.total -
                      fabricDetails?.abstract_screening?.ai_screening?.reviewed
                  }}
                  abstractScreening={fabricDetails?.abstract_screening}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "CONFLICT" && (
                <Screening
                  mode="CONFLICT"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  stats={{
                    results: fabricDetails?.abstract_screening?.conflict_resolution?.total,
                    included: fabricDetails?.abstract_screening?.conflict_resolution?.included,
                    excluded: fabricDetails?.abstract_screening?.conflict_resolution?.excluded,
                    resolved: fabricDetails?.abstract_screening?.conflict_resolution?.reviewed,
                    remaining: fabricDetails?.abstract_screening?.conflict_resolution?.total -
                      fabricDetails?.abstract_screening?.conflict_resolution?.reviewed,
                  }}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "QC" && (
                <Screening
                  mode="QC"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  stats={{
                    results: fabricDetails?.abstract_screening?.qc?.total,
                    included: fabricDetails?.abstract_screening?.qc?.included,
                    excluded: fabricDetails?.abstract_screening?.qc?.excluded,
                    resolved: fabricDetails?.abstract_screening?.qc?.reviewed,
                    remaining: fabricDetails?.abstract_screening?.qc?.total -
                      fabricDetails?.abstract_screening?.qc?.reviewed,
                  }}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "FULLTEXT_LIST" && (
                <FullTextList fabricId={fabricId} picoJson={picoJson} />
              )}

              {activeView === "LINK_FULL_TEXT" && (
                <LinkFullText fabricId={configId} picoJson={picoJson} projectName={slrFabricDetails?.stages?.general_settings?.data?.name || "SLR Project"}/>
              )}

              {activeView === "HUMAN_FT" && (
                <Screening
                  mode="HUMAN_FT"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  isOwner={fabricDetails?.user_reviewer_status?.is_owner}
                  reviewerSet={selectedReviewerSet}
                  onReviewerSetChange={(nextSet) => { setSelectedReviewerSet(nextSet); fetchFabricDetails(nextSet); }}
                  stats={fabricDetails?.full_text_screening?.human_screening}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "AI_FT" && (
                <Screening
                  mode="AI_FT"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  stats={fabricDetails?.full_text_screening?.ai_screening}
                  fullTextScreening={fabricDetails?.full_text_screening}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "CONFLICT_FT" && (
                <Screening
                  mode="CONFLICT_FT"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  stats={fabricDetails?.full_text_screening?.conflict_resolution}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "QC_FT" && (
                <Screening
                  mode="QC_FT"
                  fabricId={fabricId}
                  picoJson={picoJson}
                  framework={fabricDetails?.framework}
                  stats={fabricDetails?.full_text_screening?.qc}
                  onScreeningUpdate={fetchFabricDetails}
                />
              )}

              {activeView === "UNIQUE_STUDIES" && (
                <UniqueStudies fabricId={fabricId} picoJson={picoJson} />
              )}
            </>
          )}
        </div>
      </div>
      )}
      {showPrismaModal && (
        <PrismaModal
          show={showPrismaModal}
          onClose={() => setShowPrismaModal(false)}
          fabricId={fabricId}
        />
      )}
    </>
  );
};

export default ScreeningSpaceProjectView;